import * as React from 'react';
import Text from 'antd/lib/typography/Text';
import { IntegrationType, ReleaseAdoption, StatisticType, StatisticUnit, SubscriptionFeatureType } from '@/types';
import { displayStatValue } from '@/helpers';
import IntegrationIcon from '../icons/IntegrationIcon';
import { antGrey, runwayColors } from '../styles/globalStyles';
import { isNil } from 'lodash';
import Tooltip from 'antd/lib/tooltip';
import Progress from 'antd/lib/progress/progress';
import { PlanRestrictedFeatureWrapper } from '../planRestrictions/PlanRestrictedFeatureWrapper';

interface AdoptionRateChartProps {
    title: string;
    sessionAdoption: number;
    adoptionRates: ReleaseAdoption[];
    isMultiAPK: boolean;
    currentReleaseVersion?: string;
    stabIntegrationId?: IntegrationType;
    shouldShowDisabledUI?: boolean;
}

// Builds the per-version display labels (version string, optionally suffixed with
// the build identifier for multi-APK releases).
const adoptionDisplayTitles = (adoptionRates: ReleaseAdoption[], isMultiAPK: boolean): string[] => {
    if (!(adoptionRates?.length > 0)) {
        return [];
    }
    return adoptionRates.map((adoptionRate) => {
        if (!adoptionRate.buildIdentifier) {
            return adoptionRate.externalVersionString;
        }
        if (isMultiAPK) {
            return `${adoptionRate.externalVersionString} (${adoptionRate.buildIdentifier})`;
        }
        return `${adoptionRate.externalVersionString}`;
    });
};

// The per-version horizontal adoption bars. Extracted so both the single-provider
// AdoptionRateChart and the multi-provider carousel overview page can render the
// same bar styling.
export const AdoptionVersionBars: React.FC<{
    adoptionRates: ReleaseAdoption[];
    isMultiAPK: boolean;
    currentReleaseVersion?: string;
    shouldShowDisabledUI?: boolean;
    maxBars?: number;
}> = ({ adoptionRates, isMultiAPK, currentReleaseVersion, shouldShowDisabledUI, maxBars }) => {
    const displayTitles = adoptionDisplayTitles(adoptionRates, isMultiAPK);
    const bars = isNil(maxBars) ? adoptionRates : adoptionRates.slice(0, maxBars);

    return (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
            {bars.map((release, idx) => {
                const isCurrentRelease = currentReleaseVersion === release.releaseId;
                const adoptionRate = release.adoptionRate ?? 0;
                let percentLineFill = adoptionRate > 0 ? Math.max(adoptionRate, 5) : 0;
                if (shouldShowDisabledUI) {
                    percentLineFill = 0;
                }
                return (
                    <Tooltip
                        key={release.releaseId + idx}
                        title={`${displayTitles[idx]} - ${displayStatValue(
                            StatisticType.AdoptionRate,
                            adoptionRate,
                            StatisticUnit.Percent,
                        )}  adoption rate (sessions)`}
                        placement="right"
                    >
                        <div style={{ display: 'flex', gap: 10, alignItems: 'center', height: 18 }}>
                            <Text
                                type="secondary"
                                style={{
                                    color: isCurrentRelease ? runwayColors.primary : antGrey.grey7,
                                    fontWeight: isCurrentRelease ? 600 : undefined,
                                    fontSize: 10,
                                    marginTop: 2,
                                    flexShrink: 0,
                                    width: 60,
                                }}
                                ellipsis
                            >
                                {displayTitles[idx]}
                            </Text>
                            {!isNil(release.adoptionRate) && (
                                <Progress
                                    strokeLinecap="round"
                                    percent={percentLineFill}
                                    showInfo={false}
                                    strokeColor={isCurrentRelease ? runwayColors.primary : antGrey.grey6}
                                    trailColor={antGrey.grey4}
                                    style={{
                                        flexGrow: 1,
                                    }}
                                />
                            )}
                        </div>
                    </Tooltip>
                );
            })}
        </div>
    );
};

// The big adoption percentage value. Used standalone in AdoptionRateChart and,
// with an inline integration logo, in the carousel overview page.
export const AdoptionBigNumber: React.FC<{
    sessionAdoption?: number;
    integrationId?: IntegrationType;
    fontSize?: number;
}> = ({ sessionAdoption, integrationId, fontSize = 30 }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {integrationId && <IntegrationIcon integrationId={integrationId} />}
        <Text style={{ color: antGrey.grey8, fontSize }}>
            {sessionAdoption ?
                displayStatValue(StatisticType.AdoptionRate, sessionAdoption, StatisticUnit.Percent)
            :   '--'}
        </Text>
    </div>
);

const AdoptionRateChart: React.FC<AdoptionRateChartProps> = (props: AdoptionRateChartProps) => {
    const {
        title,
        sessionAdoption,
        adoptionRates,
        isMultiAPK,
        stabIntegrationId,
        currentReleaseVersion,
        shouldShowDisabledUI,
    } = props;

    const showPreviousReleases = !isMultiAPK; //todo: don't show previous releases on beta

    return (
        <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.Rollouts}>
            {(isDisabled) => (
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        borderRadius: '8px',
                        color: antGrey.grey7,
                        padding: '18px',
                        border: `1px solid ${antGrey.grey5}`,
                        minWidth: 232,
                        flexGrow: 1,
                        height: '100%',
                    }}
                >
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                            <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.Rollouts} requiresTooltip>
                                <Text style={{ color: antGrey.grey7 }}>{title}</Text>
                            </PlanRestrictedFeatureWrapper>
                            {isDisabled || shouldShowDisabledUI ?
                                <div
                                    style={{
                                        backgroundColor: antGrey.grey2,
                                        padding: 8,
                                        marginTop: 16,
                                        borderRadius: 4,
                                        width: '100%',
                                    }}
                                >
                                    <Text
                                        style={{
                                            color: antGrey.grey7,
                                            fontSize: 12,
                                        }}
                                    >
                                        Not available on Free plan.
                                    </Text>
                                </div>
                            :   <Text style={{ color: antGrey.grey8, fontSize: 30 }}>
                                    {sessionAdoption ?
                                        displayStatValue(
                                            StatisticType.AdoptionRate,
                                            sessionAdoption,
                                            StatisticUnit.Percent,
                                        )
                                    :   '--'}
                                </Text>
                            }
                        </div>
                        {stabIntegrationId && (
                            <div>
                                <IntegrationIcon integrationId={stabIntegrationId} />
                            </div>
                        )}
                    </div>
                    {!isDisabled && showPreviousReleases && adoptionRates?.length > 0 && (
                        <AdoptionVersionBars
                            adoptionRates={adoptionRates}
                            isMultiAPK={isMultiAPK}
                            currentReleaseVersion={currentReleaseVersion}
                            shouldShowDisabledUI={shouldShowDisabledUI}
                        />
                    )}
                </div>
            )}
        </PlanRestrictedFeatureWrapper>
    );
};

export default AdoptionRateChart;
